/**
 * Maps URL pathname patterns to human-readable screen names for analytics.
 * Used by usePageViewTracking to send consistent screen_viewed events.
 */

export interface ScreenMapping {
  pattern: RegExp;
  name: string;
  getProperties?: (pathname: string) => {
    section?: string;
    workspace_id?: string;
    project_id?: string;
  };
}

const getWorkspaceProjectFromPath = (pathname: string) => {
  const match = pathname.match(/^\/workspaces\/([^/]+)(?:\/projects\/([^/]+))?/);
  return {
    workspace_id: match?.[1],
    project_id: match?.[2],
  };
};

export const SCREEN_MAPPINGS: ScreenMapping[] = [
  { pattern: /^\/login$/, name: "Login" },
  { pattern: /^\/login-sso/, name: "Login SSO" },
  { pattern: /^\/sso\/callback/, name: "SSO callback" },
  { pattern: /^\/auth\/oauth\/callback/, name: "OAuth callback" },
  { pattern: /^\/signup/, name: "Sign up" },
  { pattern: /^\/register/, name: "Sign up" },
  { pattern: /^\/confirm_email/, name: "Confirm email" },
  { pattern: /^\/email\/verification/, name: "Email verification" },
  { pattern: /^\/verify_account/, name: "Email verification" },
  { pattern: /^\/forgot_password/, name: "Forgot password" },
  { pattern: /^\/reset_password/, name: "Reset password" },
  { pattern: /^\/join\//, name: "Join workspace" },
  { pattern: /^\/upgrade/, name: "Upgrade plan" },
  { pattern: /^\/plans/, name: "Select plan" },
  { pattern: /^\/payment/, name: "Payment" },
  { pattern: /^\/activate-account/, name: "Activate account" },
  { pattern: /^\/cancel-deletion/, name: "Cancel deletion" },
  { pattern: /^\/onboarding/, name: "Onboarding" },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/new$/,
    name: "New project",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects$/,
    name: "Projects",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/script\/compare/,
    name: "Script - Compare",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "compare",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/script\/outline/,
    name: "Script - Outline",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "outline",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/script\/snapshots/,
    name: "Script - Snapshot",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "snapshots",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/script\/breakdown/,
    name: "Script - Breakdown",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "breakdown",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/script\/coverage/,
    name: "Script - Coverage",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "coverage",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/script\/notes/,
    name: "Script - Notes",
    getProperties: (pathname) => ({ ...getWorkspaceProjectFromPath(pathname), section: "notes" }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/script\/tags/,
    name: "Script - Tags",
    getProperties: (pathname) => ({ ...getWorkspaceProjectFromPath(pathname), section: "tags" }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/script\/report/,
    name: "Script - Report",
    getProperties: (pathname) => ({ ...getWorkspaceProjectFromPath(pathname), section: "report" }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/script/,
    name: "Script",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/characters\/breakdown/,
    name: "Characters - Breakdown",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "breakdown",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/characters\/summary/,
    name: "Characters - Summary",
    getProperties: (pathname) => ({ ...getWorkspaceProjectFromPath(pathname), section: "summary" }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/characters\/actors/,
    name: "Characters - Actors",
    getProperties: (pathname) => ({ ...getWorkspaceProjectFromPath(pathname), section: "actors" }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/characters/,
    name: "Characters",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/schedule\/stripboard/,
    name: "Schedule - Stripboard",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "stripboard",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/schedule\/shootingcalendar/,
    name: "Schedule - Shooting calendar",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "shooting_calendar",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/schedule\/dayoutofdays/,
    name: "Schedule - Day out of days",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "day_out_of_days",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/schedule\/roadmap/,
    name: "Schedule - Roadmap",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "roadmap",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/schedule\/boneyard/,
    name: "Schedule - Boneyard",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "boneyard",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/schedule/,
    name: "Schedule",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/budget\/breakdown/,
    name: "Budget - Breakdown",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "breakdown",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/budget\/taxincentives/,
    name: "Budget - Tax incentives",
    getProperties: (pathname) => ({
      ...getWorkspaceProjectFromPath(pathname),
      section: "tax_incentives",
    }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/budget\/fringes/,
    name: "Budget - Fringes",
    getProperties: (pathname) => ({ ...getWorkspaceProjectFromPath(pathname), section: "fringes" }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/budget\/actuals/,
    name: "Budget - Actuals",
    getProperties: (pathname) => ({ ...getWorkspaceProjectFromPath(pathname), section: "actuals" }),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/budget/,
    name: "Budget",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings\/general/,
    name: "Project settings - General",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings\/members\/users/,
    name: "Project settings - Members",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings\/members\/roles/,
    name: "Project settings - Roles",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings\/members/,
    name: "Project settings - Members",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings\/credits/,
    name: "Project settings - AI credits",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings\/danger/,
    name: "Project settings - Danger zone",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/projects\/[^/]+\/settings/,
    name: "Project settings",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/settings\/billing/,
    name: "Workspace - Billing",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/settings\/api-keys/,
    name: "Workspace - API keys",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/settings\/sso/,
    name: "Workspace - SSO",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/settings\/members/,
    name: "Workspace - Members",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/settings\/projects/,
    name: "Workspace - Projects",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/settings\/activity/,
    name: "Workspace - Activity log",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/settings\/general/,
    name: "Workspace - General",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/settings\/account/,
    name: "Workspace - Account",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  {
    pattern: /^\/workspaces\/[^/]+\/settings$/,
    name: "Workspace settings",
    getProperties: (pathname) => getWorkspaceProjectFromPath(pathname),
  },
  { pattern: /^\/settings\/profile/, name: "Settings - Profile" },
  { pattern: /^\/settings\/security\/login-history/, name: "Settings - Login history" },
  { pattern: /^\/settings\/security\/account/, name: "Settings - Security" },
  { pattern: /^\/settings\/security/, name: "Settings - Security" },
  { pattern: /^\/settings\/account/, name: "Settings - Account" },
  { pattern: /^\/settings\/billing/, name: "Settings - Billing" },
  { pattern: /^\/settings\/referrals/, name: "Settings - Referrals" },
  { pattern: /^\/settings\/notifications/, name: "Settings - Notifications" },
  { pattern: /^\/settings/, name: "Settings" },
  { pattern: /^\/404/, name: "Not found" },
];

export const getScreenNameFromPath = (
  pathname: string
): { name: string; properties?: Record<string, string> } => {
  for (const mapping of SCREEN_MAPPINGS) {
    if (mapping.pattern.test(pathname)) {
      const properties = mapping.getProperties?.(pathname);
      const props: Record<string, string> = {};
      if (properties?.workspace_id) props.workspace_id = properties.workspace_id;
      if (properties?.project_id) props.project_id = properties.project_id;
      if (properties?.section) props.section = properties.section;
      return { name: mapping.name, properties: Object.keys(props).length ? props : undefined };
    }
  }
  return { name: pathname || "Home", properties: undefined };
};
